import React, { useEffect, useState } from "react";
import { UserCheck, Clock } from "lucide-react";
import { Page, Card, StatCard, PrimaryButton, EmptyState } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

export default function Supervision() {
  const [notes, setNotes] = useState([]);
  const [hours, setHours] = useState({ total_hours: 0, direct_hours: 0, required: 3000, progress: 0 });
  const [comment, setComment] = useState({});

  const load = () => Promise.all([
    api.get("/supervision/notes").then(({ data }) => setNotes(data)).catch(() => setNotes([])),
    api.get("/supervision/hours").then(({ data }) => setHours(data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const cosign = async (id) => {
    try {
      await api.post(`/supervision/cosign/${id}`, { comment: comment[id] || "" });
      toast.success("Co-signed");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <Page title="Supervision" subtitle="Review intern notes, co-sign, track licensure hours.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <StatCard label="Hours Logged" value={hours.total_hours} icon={Clock} accent="var(--mf-primary)" delay={0.05} testid="sup-hours-logged" />
        <StatCard label="Direct Hours" value={hours.direct_hours} icon={Clock} accent="var(--mf-secondary)" delay={0.1} testid="sup-direct" />
        <StatCard label="Required" value={hours.required} icon={Clock} accent="var(--mf-accent)" delay={0.15} testid="sup-required" />
        <StatCard label="Progress" value={`${Math.round(hours.progress * 100)}%`} icon={UserCheck} accent="#7BAE7F" delay={0.2} testid="sup-progress" />
      </div>

      <Card delay={0.25}>
        <h3 className="font-serif text-xl mb-4">Licensure Progress</h3>
        <div className="h-3 rounded-full overflow-hidden" style={{ background: "var(--mf-muted)" }}>
          <div className="h-full transition-all" style={{ width: `${Math.min(hours.progress * 100, 100)}%`, background: "var(--mf-secondary)" }}></div>
        </div>
        <div className="text-xs mt-2" style={{ color: "var(--mf-text-muted)" }}>{hours.total_hours} of {hours.required} hours</div>
      </Card>

      <div className="mt-8">
        <h2 className="font-serif text-2xl mb-4">Notes Needing Review</h2>
        {notes.length === 0 ? <EmptyState icon={UserCheck} title="All caught up" description="No intern notes pending your review." /> : (
          <div className="space-y-3">
            {notes.map((n) => (
              <Card key={n.id} testid={`review-note-${n.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium">{n.format} · {n.template}</div>
                  <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{new Date(n.created_at).toLocaleString()}</div>
                </div>
                <div className="text-sm mb-3 mf-prose whitespace-pre-wrap p-3 rounded-xl" style={{ background: "var(--mf-muted)" }}>{n.final_content}</div>
                <textarea value={comment[n.id] || ""} onChange={(e) => setComment({ ...comment, [n.id]: e.target.value })} rows={2} placeholder="Supervisor comments" className="w-full px-4 py-2.5 rounded-xl outline-none text-sm resize-none mb-3" style={{ background: "var(--mf-muted)" }} data-testid={`review-comment-${n.id}`}></textarea>
                <PrimaryButton onClick={() => cosign(n.id)} testid={`cosign-${n.id}`}>Co-sign & Approve</PrimaryButton>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Page>
  );
}
